/**
 * Voice Context - Manages voice/realtime session state.
 *
 * Provides voice mode controls (start/stop), audio pipeline management,
 * and event forwarding to the chat pipeline via onVoiceEvent.
 *
 * Chat-relevant events (transcripts, tools, tokens) are handled by useChat
 * through the onVoiceEvent callback — NOT stored here.
 */

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {
  VoiceTransport,
  type VoiceConnectionState,
} from '../transport/VoiceTransport';
import { PcmPlayback } from '../audio/pcm-playback';
import {
  registerCaptureProcessor,
  createCaptureNode,
} from '../audio/pcm-capture-processor';
import { useAgentOptional } from './AgentContext';
import { getAuthToken, useAuthContext } from './AuthContext';

const ORCHESTRATOR_URL =
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  process.env.REACT_APP_AGENT_URL ||
  'http://localhost:8090';

export type VoiceState = 'inactive' | 'connecting' | 'active' | 'error';

export interface VoiceContextValue {
  /** Start a voice session */
  startVoice: (sessionId: string, userId: string) => Promise<void>;
  /** Stop the current voice session */
  stopVoice: () => void;
  /** Whether voice mode is active */
  isVoiceActive: boolean;
  /** Current voice state */
  voiceState: VoiceState;
  /** Audio format info from session.ready */
  audioFormat: { inputSampleRate: number; outputSampleRate: number } | null;
  /** Whether user is currently speaking (VAD) */
  isUserSpeaking: boolean;
  /** Whether assistant is currently responding */
  isAssistantResponding: boolean;
  /** Register an external handler for voice events. Returns unsubscribe fn. */
  onVoiceEvent: (
    handler: (event: Record<string, unknown>) => void,
  ) => () => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

interface VoiceProviderProps {
  children: React.ReactNode;
}

export function VoiceProvider({ children }: VoiceProviderProps) {
  const [voiceState, setVoiceState] = useState<VoiceState>('inactive');
  const [audioFormat, setAudioFormat] = useState<{
    inputSampleRate: number;
    outputSampleRate: number;
  } | null>(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAssistantResponding, setIsAssistantResponding] = useState(false);

  const transportRef = useRef<VoiceTransport | null>(null);
  const playbackRef = useRef<PcmPlayback | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const captureNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletUrlRef = useRef<string | null>(null);
  const voiceStateRef = useRef<VoiceState>('inactive');
  const isInterruptedRef = useRef(false);

  // External event handler ref (set via onVoiceEvent)
  const externalHandlerRef = useRef<
    ((event: Record<string, unknown>) => void) | null
  >(null);

  const agentContext = useAgentOptional();
  const selectedAgentSlug = agentContext?.selectedAgent?.slug;
  // Spec 052 finding 4: forward the active organization id so the
  // backend's StreamContext sees the same scope text mode does. The
  // JWT also carries ``accountId``, but passing it explicitly keeps
  // the WS query consistent with the text WS endpoint.
  const auth = useAuthContext();
  const organizationId = auth.organization?.id ?? undefined;

  // Helper to update both state and ref synchronously.
  const setVoiceStateSynced = useCallback(
    (next: VoiceState) => {
      voiceStateRef.current = next;
      setVoiceState(next);
    },
    [setVoiceState],
  );

  // Handle incoming events from the voice server.
  const handleEvent = useCallback(
    (event: Record<string, unknown>) => {
      const type = event.type as string;

      // Forward ALL events to the external handler (useChat pipeline)
      externalHandlerRef.current?.(event);

      switch (type) {
        // CXS custom event sent by endpoint after agent is ready
        case 'session.ready': {
          setVoiceStateSynced('active');
          const format = event.audio_format as
            | Record<string, number>
            | undefined;
          if (format) {
            setAudioFormat({
              inputSampleRate: format.input_sample_rate ?? 24000,
              outputSampleRate: format.output_sample_rate ?? 24000,
            });
          }
          break;
        }

        // AgentScope: audio chunk from the model — play locally
        case 'agent_response_audio_delta': {
          const delta = event.delta as string;
          if (delta && playbackRef.current && !isInterruptedRef.current) {
            playbackRef.current.play(delta);
          }
          break;
        }

        // Response lifecycle (for animation state)
        // Backend translates agent_response_created → voice-turn-start
        case 'voice-turn-start': {
          isInterruptedRef.current = false;
          setIsAssistantResponding(true);
          break;
        }

        // Backend translates agent_response_done → finish (after data-activity)
        case 'finish': {
          setIsAssistantResponding(false);
          break;
        }

        // AgentScope: VAD / input lifecycle
        case 'agent_input_started': {
          setIsUserSpeaking(true);
          // Interrupt: stop playback and cancel response if assistant is responding
          if (voiceStateRef.current === 'active') {
            isInterruptedRef.current = true;
            playbackRef.current?.interrupt();
            transportRef.current?.sendResponseCancel();
            setIsAssistantResponding(false);
          }
          break;
        }

        case 'agent_input_done': {
          setIsUserSpeaking(false);
          break;
        }

        // Error events (backend translates both error + agent_error → error)
        case 'error': {
          const msg = event.errorText || event.message || event.error;
          if (voiceStateRef.current === 'active') {
            console.warn('Voice warning:', msg);
          } else {
            console.error('Voice error:', msg);
            setVoiceStateSynced('error');
          }
          break;
        }
      }
    },
    [setVoiceStateSynced],
  );

  const startVoice = useCallback(
    async (sessionId: string, userId: string) => {
      if (voiceState === 'active' || voiceState === 'connecting') return;

      setVoiceStateSynced('connecting');
      setAudioFormat(null);
      setIsUserSpeaking(false);
      setIsAssistantResponding(false);

      try {
        // Request microphone permission
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: { ideal: 24000 },
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        mediaStreamRef.current = stream;

        // Build voice endpoint URL based on agent context
        const voiceEndpoint = selectedAgentSlug
          ? `/agents/${selectedAgentSlug}/realtime`
          : '/realtime';

        // Create transport and connect
        const transport = new VoiceTransport({
          baseUrl: ORCHESTRATOR_URL,
          voiceEndpoint,
          onConnectionStateChange: (state: VoiceConnectionState) => {
            if (state === 'disconnected' || state === 'error') {
              setVoiceStateSynced(state === 'error' ? 'error' : 'inactive');
            }
          },
          onEvent: handleEvent,
        });

        transportRef.current = transport;

        // Forward the WorkOS-derived JWT so voice has the same auth
        // text-mode uses (memory service, CXS MCP, etc.). Browsers can't
        // set headers on WS upgrades, so it rides as `session_token`
        // query param — same convention the text WS uses.
        const sessionToken = getAuthToken() || undefined;
        await transport.connect(sessionId, userId, {
          sessionToken,
          organizationId: organizationId ?? undefined,
        });

        // Send session.create to initiate the voice session
        transport.sendSessionCreate();

        // Set up audio pipeline
        const audioCtx = new AudioContext({ sampleRate: 24000 });
        audioContextRef.current = audioCtx;

        // Register worklet and create capture node
        const workletUrl = await registerCaptureProcessor(audioCtx);
        workletUrlRef.current = workletUrl;

        const captureNode = createCaptureNode(audioCtx, (base64: string) => {
          transport.sendAudioInput(base64);
        });
        captureNodeRef.current = captureNode;

        // Connect mic stream to capture processor
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(captureNode);
        captureNode.connect(audioCtx.destination); // Required for processing

        // Set up playback
        playbackRef.current = new PcmPlayback(24000);
        await playbackRef.current.resume();
      } catch (err) {
        console.error('Failed to start voice:', err);
        setVoiceStateSynced('error');
        // Clean up on failure
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        transportRef.current?.dispose();
        transportRef.current = null;
      }
    },
    [voiceState, selectedAgentSlug, handleEvent, setVoiceStateSynced, organizationId],
  );

  const stopVoice = useCallback(() => {
    // Stop mic
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;

    // Stop capture
    captureNodeRef.current?.disconnect();
    captureNodeRef.current = null;

    // Close audio context
    audioContextRef.current?.close();
    audioContextRef.current = null;

    // Clean up worklet URL
    if (workletUrlRef.current) {
      URL.revokeObjectURL(workletUrlRef.current);
      workletUrlRef.current = null;
    }

    // Stop playback
    playbackRef.current?.dispose();
    playbackRef.current = null;

    // Disconnect transport
    transportRef.current?.dispose();
    transportRef.current = null;

    setVoiceStateSynced('inactive');
    setAudioFormat(null);
    setIsUserSpeaking(false);
    setIsAssistantResponding(false);
  }, [setVoiceStateSynced]);

  // Register external event handler
  const onVoiceEvent = useCallback(
    (handler: (event: Record<string, unknown>) => void): (() => void) => {
      externalHandlerRef.current = handler;
      return () => {
        externalHandlerRef.current = null;
      };
    },
    [],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
      playbackRef.current?.dispose();
      transportRef.current?.dispose();
      if (workletUrlRef.current) {
        URL.revokeObjectURL(workletUrlRef.current);
      }
    };
  }, []);

  const value = useMemo<VoiceContextValue>(
    () => ({
      startVoice,
      stopVoice,
      isVoiceActive: voiceState === 'active',
      voiceState,
      audioFormat,
      isUserSpeaking,
      isAssistantResponding,
      onVoiceEvent,
    }),
    [
      startVoice,
      stopVoice,
      voiceState,
      audioFormat,
      isUserSpeaking,
      isAssistantResponding,
      onVoiceEvent,
    ],
  );

  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
}

/**
 * Hook to access voice context.
 */
export function useVoice(): VoiceContextValue {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

/**
 * Hook to access voice context (optional).
 */
export function useVoiceOptional(): VoiceContextValue | null {
  return useContext(VoiceContext);
}
